import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import ExemptSubMenu from '@/components/nav/ExemptSubMenu';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Index = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="State Filing Requirements for US Nonprofits | TaxZerone"
          description="Learn about state filing requirements for nonprofits and how TaxZerone can help streamline your tax filing process. Stay compliant with ease!"
          keywords=""
          canonical="nonprofit-taxes/"
          // jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
          ogShareImg="assets/images/exempt-share-img.png"
        />
      }
    >
      <ExemptSubMenu />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 0, lg: 0, xl: 0 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                {' '}
                <span className="highlight-container">
                  <span className="highlight">State Filing</span>
                </span>{' '}
                Requirements: Everything You Need to Know
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Understanding and meeting state filing requirements is crucial
                for non-profits operating within the United States.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                Non-compliance can result in fines, penalties, or business
                closures. To help businesses stay legal and efficient,
                here&apos;s a guide to key state filing requirements
              </Typography>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="contained"
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990-state-filing`}
                  sx={{
                    mr: { xs: 0, md: 4 },
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                >
                  State Filing Requirements
                </Button>
              </Stack>
            </Box>
          </Box>
          <Hidden smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
              className="state-exempt-filing-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/state-landing-img.webp"
                  className="img-responsive-banner-state"
                />
                <source
                  srcSet="/assets/images/state-landing-img.png"
                  className="img-responsive-banner-state"
                />
                <img
                  src="/assets/images/state-landing-img.png"
                  alt="State Filing Requirements: Everything You Need to Know"
                  className="img-responsive-banner-state"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          {/* <Hidden lgUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
              className="state-exempt-filing-bg"
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/state-landing-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/state-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/state-landing-img-sm.png"
                  alt="State Filing Requirements: Everything You Need to Know"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden> */}
        </Stack>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%', py: 2, px: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  lineHeight: 1.5,
                  fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  textAlign: { xs: 'center', sm: 'left', md: 'left' },
                }}
              >
                Each state has its filing requirements, including rules and
                deadlines. Understanding these requirements ensures compliance
                with federal and state laws. Our platform makes the process
                easy, helping you meet deadlines and comply with state-specific
                filing regulations.
              </Typography>
            </Box>
            {/* <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 250, md: 280 },
                textAlign: 'center',
                fontSize: 18,
                lineHeight: 1.2,
              }}
            >
              <Button
                size="large"
                variant="contained"
                href="/pricing/#informationreturns"
                sx={{
                  boxShadow: 'none',
                  borderRadius: 2,
                  mb: 0.5,
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  color: '#0a7581',
                  '&:hover, &:focus, &:active': {
                    color: '#fff',
                    backgroundColor: '#172b6b',
                  },
                }}
              >
                See Pricing
              </Button>
              <Typography
                variant="body2"
                sx={{ mt: 0, mb: 0, fontSize: { xs: 11, sm: 14 } }}
              >
                For your return volume
              </Typography>
            </Box> */}
          </Stack>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why Are State Requirements Important?
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            m={2}
            mt={4}
            pb={{ xs: 0, sm: 4, md: 7 }}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/compliance-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/compliance-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/compliance-icon.png"
                      alt="Legal Compliance "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Legal Compliance
                </Typography>
                <Typography variant="body1">
                  Filing state taxes is a legal obligation for individuals and
                  businesses earning income in a state. Non-compliance can lead
                  to penalties, fines, and legal repercussions.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/financial-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/financial-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/financial-icon.png"
                      alt="Financial Credibility"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Financial Credibility
                </Typography>
                <Typography variant="body1">
                  Regularly filing your state taxes helps establish financial
                  credibility. This is particularly important when applying for
                  loans, government contracts, or visas, as authorities often
                  require proof of tax filings.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/obligation-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/obligation-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/obligation-icon.png"
                      alt="Tax Obligations "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Tax Obligations
                </Typography>
                <Typography variant="body1">
                  State taxes, such as income tax, sales tax, and unemployment
                  tax, vary significantly. Understanding and fulfilling these
                  obligations is essential for financial stability.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/good-stand-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/good-stand-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/good-stand-icon.png"
                      alt="Good Standing "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Good Standing
                </Typography>
                <Typography variant="body1">
                  Meeting state filing requirements helps maintain your business
                  in good standing with the state government, allowing you to
                  access benefits and opportunities.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/claim-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/claim-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/claim-icon.png"
                      alt="Claiming Benefits"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Claiming Benefits
                </Typography>
                <Typography variant="body1">
                  Timely filing can allow you to claim refunds or carry forward
                  losses to offset future tax liabilities. Failing to file may
                  result in losing out on these benefits.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/permit-cion.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/permit-cion.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/permit-cion.png"
                      alt="Business Licenses & Permits "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Business Licenses & Permits
                </Typography>
                <Typography variant="body1">
                  Many businesses require specific licenses and permits to
                  operate in a particular state. These can include professional
                  licenses, sales tax permits, and environmental permits.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',

          pb: 0,
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          // className="gradient-bg"
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
              fontWeight: 600,
              mb: 3,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            State Filing Requirements
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', px: { md: 16, sm: 4, xs: 0 }, mb: 2 }}
          >
            Each U.S. state has unique state filing requirements, including
            deadlines, specific information, and electronic filing mandates.
            Click the state name to learn more about its filing requirements.
          </Typography>
          <Box component="div" sx={{ mb: 2 }}>
            <TableContainer sx={{ border: '1px solid #828282' }}>
              <Table sx={{}} aria-label="Due Date Chart">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#ccc' }}>
                    <TableCell
                      sx={{
                        color: '#444',
                        fontWeight: 600,
                        width: 300,
                        fontSize: '15px !important',
                        borderRight: '1px solid #828282',
                      }}
                    >
                      State
                    </TableCell>

                    <TableCell
                      sx={{
                        color: '#444',
                        fontWeight: 600,
                        width: 300,
                        fontSize: '15px !important',
                        borderRight: '1px solid #828282',
                      }}
                    >
                      Filing Requirement
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#444',
                        fontWeight: 600,
                        width: 300,
                        fontSize: '15px !important',
                      }}
                    >
                      Deadline
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/alabama-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Alabama (AL)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file an annual report with the Secretary
                      of State and submit Form 990 to the IRS.{' '}
                      <br style={{ marginBottom: 8 }} />
                      For Unrelated Business - Nonprofits must file Form 20C
                      with a copy of Federal Form 990-T.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 15
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/alaska-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Alaska (AK){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file biennial reports with the Division
                      of Corporations. Charitable organizations must register if
                      soliciting donations
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      July 2nd for Nonprofit Organization
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/arizona-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Arizona (AZ)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Corporation
                      Commission. Organizations soliciting charitable
                      contributions must register with the Secretary of State
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 15
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/arkansas-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Arkansas (AR)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file Form CR-03 and a copy of the federal
                      990 forms. <br style={{ marginBottom: 8 }} /> Annual
                      reports are due by August 1.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      180 days after the organization&apos;s fiscal year ends
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/california-ftb-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        California (CA){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Box
                        component="ul"
                        sx={{ mb: 1.5, mr: 2, '& li': { mb: 1.2, mr: 2 } }}
                      >
                        <li>
                          Nonprofits that file Form 990, 990-EZ, or 990-PF with
                          the IRS should file CA Form 199 with the State.{' '}
                        </li>
                        <li>
                          Nonprofits that file Form 990-N with the IRS can file
                          California e-Postcard FTB 199-N with the State.
                        </li>
                        <li>
                          For Unrelated Business - Nonprofits must file Form
                          109{' '}
                        </li>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      May 15
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/colorado-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Colorado (CO){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file periodic reports with the Secretary
                      of State. Organizations soliciting charitable
                      contributions must register with the Secretary of
                      State.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Periodic reports are due on the anniversary month of
                      incorporation.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/connecticut-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Connecticut (CT)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must also file an Annual Report
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annual reports are due by the last day of the anniversary
                      month of incorporation.
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits must file Form CT‑990T
                      along with a copy of Federal Form 990-T.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year.{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/delaware-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Delaware (DE)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits need to submit an Annual Report to the Delaware
                      Division of Corporations.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Franchise tax report due March 1.{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/district-of-columbia-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        District of Columbia (DC)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit charity renewal along with a copy
                      of IRS Form 990{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Every two years at the month end of the anniversary{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must also file Form BRA-25{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 1st of every second year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/florida-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Florida (FL){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file an Annual report with the Division of
                      Corporations annually.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annual reports due by May 1.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must File form F-1120 or F-1120A
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      June 1
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={3}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/georgia-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Georgia (GA){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual registration with the
                      Secretary of State.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 1
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Organizations soliciting charitable contributions must
                      register with the Securities and Charities Division.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Must be renewed on or before the entity&apos;s expiration
                      date.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Form 990, 990-EZ, 990-PF, and for Unrelated Business -
                      Nonprofits should file Form 600-T with the state instead
                      of Form 990-T
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      15th day of the 5th month
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/hawaii-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Hawaii (HI){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file an Annual Financial Report{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month following the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Renew charitable registration with the Attorney General’s
                      office.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Must be renewed on or before the entity&apos;s expiration
                      date.
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/idaho-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Idaho (ID)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Due on the last day of registration anniversary month
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits should file Form 66
                      with the state instead of Form 990-T.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/illinois-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Illinois (IL)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must submit Illinois Charitable Organization
                      Annual Report Form AG990-IL{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Six months after the end of the organization’s fiscal
                      year-end
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/indiana-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Indiana (IN)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file business entity reports with the
                      Secretary of State every other year.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/iowa-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Iowa (IA)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file biennial reports with the Secretary
                      of State.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Biennial reports are due by April 1 of odd-numbered
                      years.{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/kansas-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Kansas (KS)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state <br style={{ marginBottom: 8 }} />
                      For Unrelated Business - Nonprofits should file Form K-120
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Due in the sixth month after the fiscal year-end.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/kentucky-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Kentucky (KY)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should include all necessary supporting
                      documentation and a copy of the federal return
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      June 30{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/louisiana-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Louisiana (LA){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      File an Annual Report with the Secretary of State and
                      renew charitable registration if soliciting funds.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annually by the incorporation anniversary month.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/maine-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Maine (ME)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      File an Annual Report with the Secretary of State and
                      renew charitable registration with the Department of
                      Professional and Financial Regulation.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      June 1
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/maryland-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Maryland (MD)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports and personal property
                      returns with the State Department of Assessments and
                      Taxation.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 15
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must submit Form COR-92 or Form COF-85 along
                      with a copy of IRS Form 990
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 4th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/massachusetts-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Massachusetts (MA){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Organizations soliciting charitable contributions must
                      register with the Attorney General&apos;s Office and file
                      Form PC.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      4.5 months after the end of the organization’s accounting
                      year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      November 1
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/michigan-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Michigan (MI){' '}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits are required to file Form CSCL/CD-2000
                      annually{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      October 1
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/minnesota-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Minnesota (MN)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State. Organizations soliciting charitable contributions
                      must register with the Attorney General&apos;s Office.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      December 31
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/mississippi-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Mississippi (MS)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 15
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/missouri-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Missouri (MO)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annual reports due by the end of the anniversary month of
                      incorporation.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/montana-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Montana (MT)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State. No state-level charitable registration requirements
                      exist.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 15{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/nebraska-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Nebraska (NE)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file biennial reports with the Secretary
                      of State.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      April 1st of every odd-numbered year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits should file Form 1120N
                      with the state instead of Form 990-T.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/nevada-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Nevada (NV)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual lists of officers with the
                      Secretary of State. Organizations soliciting charitable
                      contributions must register with the Secretary of State.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Due by the last day of the anniversary month of
                      incorporation.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/new-hampshire-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        New Hampshire (NH)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal forms to
                      the state along with Form NHCT-12{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 4th month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/new-jersey-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        New Jersey (NJ)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should file an Annual report{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      End of the month before the registration anniversary
                      month.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must submit Form CRI-200 and Form CRI 300R
                      along with a copy of IRS Form 990{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      6 months after the organization’s fiscal year end
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/new-mexico-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        New Mexico (NM)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file biennial reports with the Secretary
                      of State{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Biennial reports are due every other year based on
                      incorporation date.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/new-york-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        New York (NY)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Department
                      of State. Organizations soliciting charitable
                      contributions must register with the Attorney
                      General&apos;s Office and file annual CHAR500.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      CHAR500 is due four months and 15 days after the fiscal
                      year-end.
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/north-carolina-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        North Carolina (NC)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits should file Form
                      CD-405 with the state instead of
                      <br /> Form 990-T.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/north-dakota-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        North Dakota (ND)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annual reports due by February 1 of each year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file Annual renewal along with a copy of
                      IRS Form 990{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      September 1 of each year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/ohio-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Ohio (OH)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file statements of continued existence
                      every five years with the Secretary of State.
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Continued existence statements are due every five years
                      from the date of incorporation.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/oklahoma-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Oklahoma (OK)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit Charity Annual Renewal{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Due to the anniversary of the organization’s registration
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/oregon-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Oregon (OR)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file annual report Form CT-12 or Form
                      CT-12F{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      4 months and 15 days after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must submit an annual renewal form{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Anniversary date of registration
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={3}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/pennsylvania-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Pennsylvania (PA)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file decennial reports with the
                      Department of State.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Decennial reports are due every ten years
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Organizations soliciting charitable contributions must
                      register with the Bureau of Charitable Organizations.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Charitable reports are due based on fiscal year-end.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file Form BCO-10 along with a copy of IRS
                      Form 990{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/rhode-island-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        Rhode Island (RI)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits are required to File Annual reports{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Between February 1st and May 1st each year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits must file Form
                      RI-1120C with the state instead of Form 990-T.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 3rd month after the end of the
                      organization&apos;s fiscal year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/south-carolina-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        South Carolina (SC)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state <br style={{ marginBottom: 8 }} />
                      For Unrelated Business - Nonprofits must file Form SC990-T
                      with the state instead of Form 990-T
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/south-dakota-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        South Dakota (SD)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State. No state-level charitable registration requirements
                      exist.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      1st day of the organization’s anniversary month{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/tennessee-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Tennessee (TN)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must renew their charity registration along
                      with a copy of IRS Form 990{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Last day of the 6th month after the end of the
                      organization&apos;s accounting year{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State. Organizations soliciting charitable contributions
                      must register with the Secretary of State
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      1st day of the 4th month following the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/texas-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Texas (TX)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file periodic reports with the Secretary
                      of State and Comptroller. Organizations soliciting
                      charitable contributions must maintain state tax
                      exemption.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Periodic reports are due every four years; franchise tax
                      reports are due May 15.{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/utah-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Utah (UT)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits must file Form TC-20MC
                      with the state instead of Form 990-T.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 4th month after the end of the
                      organization&apos;s fiscal year.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Division of
                      Corporations.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annual reports due during the anniversary month of
                      incorporation.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/vermont-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Vermont (VT)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file biennial reports with the Secretary
                      of State.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Biennial reports are due between January 1 and April 15 of
                      even-numbered years.
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      For Unrelated Business - Nonprofits must file Form
                      CO-411{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/virginia-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Virginia (VA)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 6th month after the end of the
                      organization&apos;s fiscal year
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/washington-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Washington (WA)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Annual reports due by the last day of the anniversary
                      month of incorporation.{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should submit the respective federal 990 forms
                      to the state.{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      The 15th day of the 5th month after the end of the
                      organization&apos;s fiscal year.{' '}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      {' '}
                      <Link
                        href="/nonprofit-taxes/west-virginia-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        West Virginia (WV)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file an Annual Report Nonprofits must file
                      Annual renewal Form CHR-1 with a copy of IRS FOrm 990
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Between January 1 and June 30 Due to the anniversary date
                      of registration
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/wisconsin-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Wisconsin (WI)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits must file Annual Renewal{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      July 31st
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      Nonprofits should file annual financial reports Form 1943,
                      Form 1952, or Form 308{' '}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #828282',
                      }}
                    >
                      12 months after the organization’s fiscal year-end
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{}}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                      }}
                    >
                      <Link
                        href="/nonprofit-taxes/wyoming-state-filing-requirements/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Wyoming (WY)
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: '1px solid #828282',
                      }}
                    >
                      Non-profits must file annual reports with the Secretary of
                      State. No state-level charitable registration requirements
                      exist.{' '}
                    </TableCell>
                    <TableCell>
                      Annual reports due by the first day of the anniversary
                      month of incorporation.{' '}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Container>
      </Grid>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          sx={{ my: { xs: 2, sm: 6, md: 8, justifyContent: 'center' } }}
          className="MuiGrid-root-width"
        >
          <Grid size={12} textAlign="center">
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Why Choose TaxZerone?
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                px: 3,
                pt: 5,
                pb: 5,
                textAlign: 'center',
                borderWidth: 3,
                borderColor: '#8492c0',
                borderRadius: 10,
                minHeight: '100%',
                backgroundColor: '#efefef',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  width: 90,
                  height: 90,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mx: 'auto',
                }}
              >
                <picture style={{ width: 60, height: 60 }}>
                  <source
                    srcSet="/assets/images/secure-icon.webp"
                    width={60}
                    height={60}
                  />
                  <source
                    srcSet="/assets/images/secure-icon.png"
                    width={60}
                    height={60}
                  />
                  <img
                    src="/assets/images/secure-icon.png"
                    alt="Data Security "
                    loading="lazy"
                    width={60}
                    height={60}
                  />
                </picture>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  fontWeight: 600,
                  my: 3,
                }}
              >
                Data Security
              </Typography>
              <Typography variant="body1">
                We prioritize your data security with advanced encryption and
                protection measures. Trust us to keep your sensitive information
                safe while you focus on your business.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                px: 3,
                pt: 5,
                pb: 5,
                textAlign: 'center',
                borderWidth: 3,
                borderColor: '#76ce97',
                borderRadius: 10,
                minHeight: '100%',
                backgroundColor: '#efefef',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  width: 90,
                  height: 90,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mx: 'auto',
                }}
              >
                <picture style={{ width: 60, height: 60 }}>
                  <source
                    srcSet="/assets/images/support-icon.webp"
                    width={60}
                    height={60}
                  />
                  <source
                    srcSet="/assets/images/support-icon.png"
                    width={60}
                    height={60}
                  />
                  <img
                    src="/assets/images/support-icon.png"
                    alt="Dedicated Support"
                    loading="lazy"
                    width={60}
                    height={60}
                  />
                </picture>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  fontWeight: 600,
                  my: 3,
                }}
              >
                Dedicated Support
              </Typography>
              <Typography variant="body1">
                Access our responsive support team for any questions during the
                filing process. We also provide valuable resources and guides to
                help you navigate your state filing with ease.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                px: 3,
                pt: 5,
                pb: 5,
                textAlign: 'center',
                borderWidth: 3,
                borderColor: '#8492c0',
                borderRadius: 10,
                minHeight: '100%',
                backgroundColor: '#efefef',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '50%',
                  width: 90,
                  height: 90,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mx: 'auto',
                }}
              >
                <picture style={{ width: 60, height: 60 }}>
                  <source
                    srcSet="/assets/images/easy-icon.webp"
                    width={60}
                    height={60}
                  />
                  <source
                    srcSet="/assets/images/easy-icon.png"
                    width={60}
                    height={60}
                  />
                  <img
                    src="/assets/images/easy-icon.png"
                    alt="Quick & Easy Filing"
                    loading="lazy"
                    width={60}
                    height={60}
                  />
                </picture>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  fontWeight: 600,
                  my: 3,
                }}
              >
                Quick & Easy Filing
              </Typography>
              <Typography variant="body1">
                Our user-friendly platform is designed to simplify your e-filing
                process. From clear instructions to smart error checks, we guide
                you every step of the way, making the entire filing process
                stress-free!
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          Choose TaxZerone for a reliable, efficient state filing experience!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          Ready to Ensure Compliance?
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990state-filing`}
          >
            Get Started
          </Button>
        </Box>
      </Box>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. Why do I need to file state taxes?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Filing state taxes is essential for legal compliance,
                    establishing financial credibility, claiming benefits, and
                    ensuring transparency in financial dealings.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Do I need to file state taxes if I’m a non-resident?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    If you earned income in the state, you might need to file a
                    non-resident state tax return. Check the specific state’s
                    rules to know more about your filing requirements.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. How do I know which forms I need to file?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    The specific forms required depend on your state of
                    residence and your individual or business circumstances;
                    consult your state&apos;s Department of Revenue or a tax
                    professional for guidance.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. How do state filing deadlines differ from federal
                  deadlines?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    While most states align with the federal April 15 deadline,
                    some have unique deadlines. Always verify with your state’s
                    tax authority to know more about your filing requirements.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Index;
